import { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Bookmark, Flame, Calendar, MapPin, Sparkles, Clock, TrendingUp, LayoutGrid, List } from "lucide-react";
import { TopNav } from "../components/TopNav";
import { SpotCard } from "../components/SpotCard";
import { DayByDay } from "../components/DayByDay";
import { Copilot } from "../components/Copilot";
import { ShareButton } from "../components/ShareButton";
import { ShareCardButton } from "../components/ShareCard";
import { MoodBoard } from "../components/MoodBoard";
import { ViralScore } from "../components/ViralScore";
import { CreatorBrief } from "../components/CreatorBrief";
import { fetchItinerary, saveItinerary } from "../lib/api";
import { localSavedStore } from "../lib/savedStore";
import { useAuth } from "../lib/AuthContext";
import { toast } from "sonner";

export default function ItineraryPage() {
  const { id } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [itin, setItin] = useState(state?.itin || null);
  const [loading, setLoading] = useState(!state?.itin);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [view, setView] = useState("days"); // days | grid

  useEffect(() => {
    if (itin?.id) setSaved(localSavedStore.has(itin.id));
  }, [itin?.id]);

  useEffect(() => {
    const loadItinerary = async () => {
      if (state?.itin) {
        // Re-fetch to ensure backfilled day/time fields are present
        try {
          const data = await fetchItinerary(id);
          setItin(data);
        } catch (err) {
          // Silent fail for refetch
        }
        return;
      }
      setLoading(true);
      try {
        const data = await fetchItinerary(id);
        setItin(data);
      } catch (err) {
        toast.error("Could not load this trip");
      } finally {
        setLoading(false);
      }
    };
    
    loadItinerary();
  }, [id, state]);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Always persist to local storage so guests can save too
      localSavedStore.upsert(itin);
      // If logged in, also save server-side
      if (user) {
        try { await saveItinerary(itin); } catch (_) { /* server save optional */ }
      }
      setSaved(true);
      toast.success(user ? "Saved to your trips" : "Saved on this device");
    } catch (e) {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !itin) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center">
        <TopNav />
        <div className="flex items-center gap-3 text-zinc-400">
          <Sparkles className="h-5 w-5 animate-pulse text-[#00F2FE]" /> Curating your viral itinerary…
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <TopNav />

      {/* HERO COVER */}
      <section className="relative w-full">
        <div className="relative h-[60svh] sm:h-[75svh] w-full overflow-hidden">
          <img src={itin.cover_image} alt={itin.title} className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 scrim-full" />
          <div className="absolute inset-0 grain" />
        </div>

        <div className="absolute inset-x-0 bottom-0 z-10">
          <div className="mx-auto max-w-5xl px-5 sm:px-8 pb-8 sm:pb-12">
            <Link to="/" data-testid="back-home" className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-zinc-300 hover:text-white mb-4">
              <ArrowLeft className="h-3.5 w-3.5" /> Back
            </Link>

            <div className="flex flex-wrap items-center gap-2 mb-4">
              <span className="glass rounded-full px-2.5 py-1 inline-flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5 text-zinc-300" />
                <span className="text-[10px] font-bold uppercase tracking-[0.18em]">{itin.duration_days} days</span>
              </span>
              <span className="glass rounded-full px-2.5 py-1 inline-flex items-center gap-1.5">
                <MapPin className="h-3.5 w-3.5 text-zinc-300" />
                <span className="text-[10px] font-bold uppercase tracking-[0.18em]">{itin.destination}</span>
              </span>
              {itin.content_potential && (
                <span className="glass rounded-full px-2.5 py-1 inline-flex items-center gap-1.5 ring-1 ring-[#FE0979]/40">
                  <TrendingUp className="h-3.5 w-3.5 text-[#FE0979]" />
                  <span className="text-[10px] font-bold uppercase tracking-[0.18em]">{itin.content_potential} content</span>
                </span>
              )}
            </div>

            <div className="mb-5">
              <ViralScore
                breakdown={itin.viral_breakdown}
                fallbackScore={itin.viral_score}
                size="lg"
                testId="itinerary-viral-score"
              />
            </div>

            <motion.h1
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              data-testid="itinerary-title"
              className="font-display font-black tracking-tight text-4xl sm:text-6xl leading-[0.98] text-shadow-soft max-w-3xl"
            >
              {itin.title}
            </motion.h1>
            {itin.subtitle && <p className="mt-3 text-zinc-200 text-sm sm:text-base max-w-2xl">{itin.subtitle}</p>}

            <div className="mt-5 flex flex-wrap gap-1.5">
              {(itin.vibe_tags || []).map((t) => (
                <span key={t} className="text-[10px] font-bold uppercase tracking-[0.18em] bg-white/10 border border-white/15 rounded-full px-2.5 py-1">
                  {t}
                </span>
              ))}
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <button
                data-testid="save-itinerary-btn"
                onClick={handleSave}
                disabled={saving || saved}
                className="gradient-pink-cyan text-black font-display font-bold uppercase tracking-[0.18em] text-xs rounded-full px-5 py-3 inline-flex items-center gap-2 hover:opacity-95 active:scale-[0.98] transition disabled:opacity-70"
              >
                <Bookmark className="h-4 w-4" /> {saved ? "Saved" : saving ? "Saving…" : "Save trip"}
              </button>
              <ShareButton
                itineraryId={itin.id}
                title={itin.title}
                subtitle={itin.subtitle}
                variant="glass"
              />
              <ShareCardButton itinerary={itin} />
              <MoodBoard itinerary={itin} />
            </div>
          </div>
        </div>
      </section>

      {/* SUMMARY STRIP */}
      <section className="px-5 sm:px-8 pt-10 sm:pt-14">
        <div className="mx-auto max-w-5xl grid grid-cols-2 sm:grid-cols-4 gap-3">
          <SummaryCard label="Viral score" value={`${itin.viral_score}/100`} icon={<Flame className="h-3.5 w-3.5 text-[#FE0979]" />} />
          <SummaryCard label="Duration" value={`${itin.duration_days} days`} icon={<Calendar className="h-3.5 w-3.5 text-[#00F2FE]" />} />
          <SummaryCard label="Best time to visit" value={itin.best_time_to_visit || "Year-round"} icon={<Clock className="h-3.5 w-3.5 text-[#00F2FE]" />} />
          <SummaryCard label="Content potential" value={itin.content_potential || "HIGH"} icon={<TrendingUp className="h-3.5 w-3.5 text-[#FE0979]" />} />
        </div>
      </section>

      {/* DAY-BY-DAY */}
      <section className="px-5 sm:px-8 py-12 sm:py-16">
        <div className="mx-auto max-w-6xl">
          <div className="flex items-end justify-between gap-4 mb-8 flex-wrap">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#00F2FE] mb-2">Reel itinerary</div>
              <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight">Your day-by-day shoot plan</h2>
            </div>
            <div className="glass rounded-full p-1 inline-flex items-center gap-1">
              <button
                onClick={() => setView("days")}
                data-testid="view-days"
                className={`text-[10px] font-bold uppercase tracking-[0.2em] rounded-full px-3 py-2 inline-flex items-center gap-1.5 transition ${view === "days" ? "bg-white text-black" : "text-zinc-300 hover:text-white"}`}
              >
                <List className="h-3 w-3" /> Days
              </button>
              <button
                onClick={() => setView("grid")}
                data-testid="view-grid"
                className={`text-[10px] font-bold uppercase tracking-[0.2em] rounded-full px-3 py-2 inline-flex items-center gap-1.5 transition ${view === "grid" ? "bg-white text-black" : "text-zinc-300 hover:text-white"}`}
              >
                <LayoutGrid className="h-3 w-3" /> Grid
              </button>
            </div>
          </div>

          {view === "days" ? (
            <DayByDay itinerary={itin} />
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-5">
              {(itin.spots || []).map((s, i) => (
                <SpotCard key={s.id} spot={s} index={i} onClick={() => navigate(`/spot/${itin.id}/${s.id}`)} />
              ))}
            </div>
          )}
        </div>
      </section>

      <Copilot itineraryId={itin.id} itineraryTitle={itin.title} />

      <CreatorBrief itinerary={itin} />

      <footer className="px-5 py-10 text-center text-xs text-zinc-500">
        Curated by VYBE AI · Tap any spot for the angle, caption & Maps link.
      </footer>
    </div>
  );
}

const SummaryCard = ({ label, value, icon }) => (
  <div className="glass rounded-2xl p-4">
    <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-400 flex items-center gap-1.5">
      {icon} {label}
    </div>
    <div className="mt-1.5 font-display font-bold text-base sm:text-lg leading-tight">{value}</div>
  </div>
);
